import { selectClient } from '@client/model/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook to access pre-calculated coverage assessment data from the Client object.
 * Uses backend-calculated values instead of performing recalculations.
 */
export function useCoverageAssessmentData() {
  const client = useSelector(selectClient) as Client;

  // Round yearsTillLtc for display purposes
  const yearsTillLtc = useMemo(
    () => Math.round(client.appliedInferenceSet.yearsTillLtc),
    [client.appliedInferenceSet.yearsTillLtc],
  );

  // Coverage is measured against the full cost the client faces — care plus
  // out-of-pocket room & board (IL only); room & board is never coverable, so
  // IL coverage can never reach 100%.
  const totalLtcCost = client.allPhaseCosts.allPhaseInflatedTotalCost;

  const multipleSelfFundingProjectedCostCoverage =
    client.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0;

  // Calculate the gap between cost and coverage
  const coverageGap = useMemo(
    () => Math.max(0, totalLtcCost - multipleSelfFundingProjectedCostCoverage),
    [totalLtcCost, multipleSelfFundingProjectedCostCoverage],
  );

  // Count of self-funding sources
  const selfFundingSourceCount = useMemo(
    () => Object.keys(client.multipleFundingSources?.selfFunding ?? {}).length,
    [client.multipleFundingSources?.selfFunding],
  );

  return {
    yearsTillLtc,
    totalLtcCost,
    multipleSelfFundingProjectedCostCoverage,
    coverageGap,
    selfFundingSourceCount,
  };
}
